import React from "react";
import { useI18n } from "../i18n";
import { Globe, Layers, FlaskConical, MapPin, Bot, ShieldCheck, BookOpen, Wallet, Mic, Volume2, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";

const SECTIONS = (lang) => [
  {
    id: "market", icon: Globe, color: "text-gold-400",
    title: lang === "pt" ? "Mercado Global" : "Global Market",
    what: lang === "pt"
      ? "Cotações ao vivo de 17 moedas, top 20 criptomoedas e 12 bolsas de valores do mundo."
      : "Live quotes for 17 currencies, top 20 cryptos and 12 world exchanges.",
    how: lang === "pt"
      ? ["Veja a taxa atual do dólar, euro, yuan, yen, rupia.",
         "Acompanhe bolsa de Nova York, Tóquio, Mumbai, Londres.",
         "Clique em qualquer moeda para ver o gráfico de 30 dias.",
         "Use o Conversor Universal para saber quanto vale $10 mil em rúpias, por exemplo."]
      : ["Check current USD, EUR, CNY, JPY, INR rates.",
         "Track NYSE, Tokyo, Mumbai, London exchanges.",
         "Click any currency for a 30-day chart.",
         "Use the Universal Converter to see what $10k is in rupees."],
    for_who: lang === "pt"
      ? "Para quem quer saber como o mundo está hoje antes de tomar qualquer decisão."
      : "For anyone who wants to know how the world is today before any decision.",
    link: "/mercado",
  },
  {
    id: "comparator", icon: Layers, color: "text-gold-300",
    title: lang === "pt" ? "Comparador Cruzado" : "Cross-Asset Comparator",
    what: lang === "pt"
      ? "Coloque lado a lado, ao mesmo tempo, até 6 investimentos totalmente diferentes."
      : "Put up to 6 completely different investments side-by-side, at once.",
    how: lang === "pt"
      ? ["Escolha uma casa em Lisboa + Bitcoin + S&P 500 + ouro.",
         "Defina quanto você investiria em cada um (em dólares).",
         "Veja o gráfico de projeção para 10 anos.",
         "Um 'takeaway' em português te diz qual seria o melhor no cenário base.",
         "Compartilhe a comparação com 1 clique — link pronto."]
      : ["Pick a house in Lisbon + Bitcoin + S&P 500 + gold.",
         "Set how much you'd invest in each (in dollars).",
         "See the 10-year projection chart.",
         "A plain-English 'takeaway' tells you the base-case winner.",
         "Share the comparison with one click — ready link."],
    for_who: lang === "pt"
      ? "Para quem está em dúvida entre tipos completamente diferentes de investimento."
      : "For anyone stuck between completely different investment types.",
    link: "/comparador",
  },
  {
    id: "simulator", icon: FlaskConical, color: "text-gold-400",
    title: lang === "pt" ? "Simulador 'E se…'" : "'What if…' Simulator",
    what: lang === "pt"
      ? "Projete em 3 cenários (pessimista, base, otimista) o que seu dinheiro renderia."
      : "Project 3 scenarios (bear, base, bull) of what your money would yield.",
    how: lang === "pt"
      ? ["Escolha o ativo (ex.: imóvel em Xangai).",
         "Informe o valor em dólares e quantas unidades.",
         "Ajuste o horizonte (1 a 25 anos).",
         "Veja 3 gráficos simultâneos: tudo pode dar certo, médio ou errado.",
         "O takeaway final explica em uma frase o que esperar."]
      : ["Pick the asset (e.g., apartment in Shanghai).",
         "Enter amount in USD and number of units.",
         "Adjust horizon (1 to 25 years).",
         "See 3 simultaneous charts: best, base, worst case.",
         "The final takeaway explains in one sentence what to expect."],
    for_who: lang === "pt"
      ? "Para quem quer saber ANTES de investir: 'se eu tivesse colocado aqui há 10 anos, quanto teria hoje?'"
      : "For anyone who wants to know BEFORE investing: 'what would I have today?'",
    link: "/simulador",
  },
  {
    id: "geography", icon: MapPin, color: "text-gold-300",
    title: lang === "pt" ? "Análise Geográfica" : "Geographic Analysis",
    what: lang === "pt"
      ? "25 países mapeados com dados de imóveis, bolsa, risco, crescimento e até depreciação de carros."
      : "25 countries mapped with real estate, stocks, risk, growth and even car depreciation data.",
    how: lang === "pt"
      ? ["Clique em qualquer país (EUA, Portugal, Dubai, Japão, Brasil…).",
         "Veja o PIB, inflação, clima de negócios e nota de risco.",
         "Veja preços de imóveis por m² em suas principais cidades, com população.",
         "Descubra quanto um carro desvaloriza em 5 anos naquele país.",
         "Gráficos de projeção de bolsa e imóveis para 10 anos."]
      : ["Click any country (US, Portugal, Dubai, Japan, Brazil…).",
         "See GDP, inflation, business climate and risk score.",
         "See real estate prices per m² in main cities, with population.",
         "Learn how much a car depreciates in 5 years there.",
         "10-year stock and real estate projection charts."],
    for_who: lang === "pt"
      ? "Para quem pensa em diversificar fora do seu país ou se mudar."
      : "For anyone thinking about diversifying abroad or relocating.",
    link: "/geografia",
  },
  {
    id: "ai", icon: Bot, color: "text-gold-400",
    title: lang === "pt" ? "Assistente IA (com voz)" : "AI Assistant (voice-enabled)",
    what: lang === "pt"
      ? "Claude Sonnet 4.5, o modelo de IA mais avançado da Anthropic, focado em decisões financeiras."
      : "Claude Sonnet 4.5, Anthropic's most advanced AI, tuned for financial decisions.",
    how: lang === "pt"
      ? ["Pergunte em texto ou aperte o microfone para falar.",
         "Ex.: 'Vale a pena comprar casa em Lisboa hoje?'",
         "A IA responde em 8 idiomas (PT, EN, ES, ZH, DE, FR, JA, HI).",
         "Toque no botão 🔊 para ouvir a resposta em áudio.",
         "Toda resposta termina com um Takeaway claro."]
      : ["Ask in text or press the mic to talk.",
         "E.g.: 'Is buying a house in Lisbon worth it today?'",
         "AI replies in 8 languages (PT, EN, ES, ZH, DE, FR, JA, HI).",
         "Tap the 🔊 button to hear the answer in audio.",
         "Every answer ends with a clear Takeaway."],
    for_who: lang === "pt"
      ? "Para quem quer um segundo ponto de vista inteligente e não-vendedor."
      : "For anyone wanting a smart, non-selling second opinion.",
    link: "/assistente",
  },
  {
    id: "profile", icon: ShieldCheck, color: "text-gold-300",
    title: lang === "pt" ? "Perfil de Risco" : "Risk Profile",
    what: lang === "pt"
      ? "Descubra em 5 perguntas se você é Conservador, Moderado, Balanceado, Crescimento ou Arrojado."
      : "In 5 questions find out if you're Conservative, Moderate, Balanced, Growth or Aggressive.",
    how: lang === "pt"
      ? ["Responda 5 perguntas rápidas (60 segundos).",
         "Veja seu perfil + alocação sugerida (renda fixa / ações / imóveis / alternativos).",
         "Nenhuma pergunta sobre patrimônio. Zero dados pessoais armazenados."]
      : ["Answer 5 quick questions (60 seconds).",
         "Get your profile + suggested allocation (fixed income / stocks / real estate / alternatives).",
         "Zero net-worth questions. Zero personal data stored."],
    for_who: lang === "pt"
      ? "Para se posicionar antes de começar a investir — sem entregar nenhum dado a ninguém."
      : "To position yourself before investing — without handing data to anyone.",
    link: "/perfil",
  },
  {
    id: "glossary", icon: BookOpen, color: "text-gold-400",
    title: lang === "pt" ? "Glossário" : "Glossary",
    what: lang === "pt"
      ? "Todos os termos financeiros explicados em 1 linha, sem jargão."
      : "Every financial term explained in one line, no jargon.",
    how: lang === "pt"
      ? ["Não sabe o que é ETF, REIT, CDI, Hedge, Beta, Drawdown?",
         "Busque o termo — 28 verbetes prontos, em português e inglês."]
      : ["Not sure what ETF, REIT, Hedge, Beta, Drawdown mean?",
         "Search the term — 28 entries ready, in English and Portuguese."],
    for_who: lang === "pt"
      ? "Para quem está começando ou quer refrescar."
      : "For beginners or anyone refreshing.",
    link: "/glossario",
  },
];

export default function HowItWorks() {
  const { lang } = useI18n();
  const sections = SECTIONS(lang);
  return (
    <div className="max-w-[1200px] mx-auto px-6 lg:px-10 pt-28 pb-16" data-testid="page-how-it-works">
      <div className="text-center">
        <div className="inline-flex items-center gap-2 rounded-full border border-gold-500/30 bg-gold-500/5 px-3 py-1 text-xs uppercase tracking-[0.2em] text-gold-400 font-mono">
          {lang === "pt" ? "Como funciona" : "How it works"}
        </div>
        <h1 className="mt-6 font-display text-4xl sm:text-5xl tracking-tight">
          {lang === "pt" ? "7 ferramentas. 1 objetivo: você decidindo sozinho." : "7 tools. 1 goal: you deciding on your own."}
        </h1>
        <p className="mt-4 max-w-2xl mx-auto text-neutral-400 text-lg">
          {lang === "pt"
            ? "Cada área da GOLDORA foi pensada para substituir uma conversa com um assessor. Veja abaixo o que cada uma faz e por onde começar."
            : "Each GOLDORA area was designed to replace a conversation with an advisor. See below what each does and where to start."}
        </p>
      </div>

      <div className="mt-14 space-y-8">
        {sections.map((s, idx) => {
          const Icon = s.icon;
          return (
            <div
              key={s.id}
              data-testid={`howto-${s.id}`}
              className="surface p-6 md:p-8 grid md:grid-cols-[auto_1fr] gap-6"
            >
              <div className="flex md:flex-col items-center gap-3 md:gap-2">
                <div className="w-14 h-14 rounded-md bg-white/5 border border-gold-500/25 grid place-items-center">
                  <Icon size={24} className={s.color} />
                </div>
                <div className="font-mono text-xs text-neutral-500 uppercase tracking-widest">
                  0{idx + 1}
                </div>
              </div>
              <div>
                <h2 className="font-display text-2xl sm:text-3xl tracking-tight">{s.title}</h2>
                <p className="mt-2 text-neutral-300 leading-relaxed">{s.what}</p>

                <div className="mt-5 grid md:grid-cols-2 gap-5">
                  <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-gold-400 font-mono">
                      {lang === "pt" ? "O que você pode fazer" : "What you can do"}
                    </div>
                    <ul className="mt-2.5 space-y-1.5 text-sm text-neutral-300">
                      {s.how.map((h, i) => (
                        <li key={i} className="flex gap-2">
                          <CheckCircle2 size={14} className="text-gold-500 shrink-0 mt-0.5" />
                          <span>{h}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-gold-400 font-mono">
                      {lang === "pt" ? "Para quem é" : "Who it's for"}
                    </div>
                    <p className="mt-2.5 text-sm text-neutral-300 leading-relaxed">{s.for_who}</p>
                    <Link
                      to={s.link}
                      data-testid={`howto-cta-${s.id}`}
                      className="mt-5 inline-flex items-center gap-1.5 text-sm text-gold-300 hover:text-gold-200 border-b border-gold-500/30 hover:border-gold-400 pb-0.5 transition-colors"
                    >
                      {lang === "pt" ? "Abrir esta seção" : "Open this section"} →
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Voice feature highlight */}
      <div className="mt-16 surface p-6 md:p-10 grid md:grid-cols-2 gap-8 items-center">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-gold-400 font-mono">
            {lang === "pt" ? "Superpoder embutido" : "Built-in superpower"}
          </div>
          <h3 className="mt-3 font-display text-3xl tracking-tight">
            {lang === "pt" ? "Pergunte falando. Receba a resposta em áudio." : "Ask by voice. Get the answer in audio."}
          </h3>
          <p className="mt-3 text-neutral-400 leading-relaxed">
            {lang === "pt"
              ? "Em qualquer tela do Assistente, toque no microfone e fale em português, inglês, espanhol, chinês, alemão, francês, japonês ou hindi. A IA transcreve (Whisper), responde (Claude 4.5) e pode ler a resposta em voz alta (TTS). Perfeito para usar no celular entre consultas, treinos ou reuniões."
              : "On any Assistant screen, tap the mic and speak in any of 8 languages. The AI transcribes (Whisper), answers (Claude 4.5) and can read the answer back (TTS). Perfect for use on your phone between meetings."}
          </p>
        </div>
        <div className="flex items-center justify-center gap-6">
          <div className="w-24 h-24 rounded-full bg-gold-500/10 border border-gold-500/40 grid place-items-center text-gold-400">
            <Mic size={36} />
          </div>
          <div className="text-gold-500 font-brand text-3xl">↔</div>
          <div className="w-24 h-24 rounded-full bg-gold-500/10 border border-gold-500/40 grid place-items-center text-gold-400">
            <Volume2 size={36} />
          </div>
        </div>
      </div>
    </div>
  );
}
